import type { BulkTripRejectReason } from '@bambi/api-client/schemas';
import { useTripsBulkRejectReasonsCreateQuery } from '@bambi/api-client/trips';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

import { useRejectTripIds } from './useRejectionReasonOptions';

// Trips with an empty reason list can't be rejected at all; including them
// would empty the intersection, so they're excluded (mirrors
// useGlobalCancelReasonOptions, where the backend returns no reasons for
// uncancelable trips).
function intersectRejectReasons(
  results: BulkTripRejectReason[] | undefined
): SelectOption[] {
  const eligible = (results ?? []).filter(
    (result) => result.rejection_reasons.length > 0
  );
  if (eligible.length === 0) {
    return [];
  }

  const [first, ...rest] = eligible.map((result) => result.rejection_reasons);
  return first
    .filter((reason) =>
      rest.every((reasons) =>
        reasons.some((other) => other.value === reason.value)
      )
    )
    .map((reason) => ({ label: reason.label, value: reason.value || '' }));
}

/**
 * Subscribes to the same bulk-reject-reasons query as the per-row selects
 * (no extra request) and intersects across rejectable trips.
 */
export function useGlobalRejectReasonOptions(): { options: SelectOption[] } {
  const tripIds = useRejectTripIds();

  return useTripsBulkRejectReasonsCreateQuery(
    { bulkTripRejectReasonsWritable: { trip_ids: tripIds } },
    {
      // The menu never renders below two trips, so don't join the query
      // for fewer — it'd be a guaranteed-discarded request.
      skip: tripIds.length < 2,
      selectFromResult: (result) => ({
        ...result,
        options: intersectRejectReasons(result.data?.results),
      }),
    }
  );
}
